import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { IconButton, PencilIcon, TrashIcon } from '../../components/ui/icons';
import { formatPhoneInput, displayPhone } from '../../lib/phone';
import { fmtDate } from '../../lib/dates';

// Service Providers directory — list + add/edit/delete in one screen.
// Viewable by staff; add/edit/delete gated to owner/manager.
export default function ServiceProviders() {
  const { activeRole } = useAuth();
  const isStaff = ['owner', 'manager', 'trainer', 'groom'].includes(activeRole);
  const canManage = activeRole === 'owner' || activeRole === 'manager';
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // null | 'new' | provider object

  const { data, isLoading, isError } = useQuery({
    queryKey: ['providers'],
    queryFn: () => api.get('/providers'),
    enabled: isStaff,
  });

  const remove = useMutation({
    mutationFn: (id) => api.del(`/providers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['providers'] }),
  });

  if (!isStaff) return <div className="p-4 text-ink-500">Service providers are for barn staff.</div>;
  if (isLoading) return <div className="p-4 text-ink-400">Loading…</div>;
  if (isError) return <div className="p-4 text-red-600">Couldn’t load providers.</div>;

  const providers = data?.providers ?? [];

  return (
    <div className="max-w-3xl mx-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Service Providers</h1>
        {canManage && editing === null && (
          <button onClick={() => setEditing('new')} className="btn-primary">
            Add provider
          </button>
        )}
      </div>

      {editing === 'new' && <ProviderForm onClose={() => setEditing(null)} />}

      {providers.length === 0 && editing === null && (
        <p className="text-ink-400">No service providers yet.</p>
      )}

      <ul className="space-y-2">
        {providers.map((p) =>
          editing && editing.id === p.id ? (
            <li key={p.id}>
              <ProviderForm provider={p} onClose={() => setEditing(null)} />
            </li>
          ) : (
            <li key={p.id} className="card p-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-ink-900">{p.name}</span>
                  {p.specialties?.map((s) => (
                    <span
                      key={s}
                      className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700"
                    >
                      {s}
                    </span>
                  ))}
                </div>
                {p.businessName && <div className="text-sm text-ink-600">{p.businessName}</div>}
                <div className="text-sm text-ink-500 mt-0.5">
                  {(p.phones ?? []).map((ph, i) => (
                    <div key={i}>
                      {ph.label ? `${ph.label}: ` : ''}
                      {displayPhone(ph.number)}
                    </div>
                  ))}
                </div>
                {p.email && <div className="text-sm text-ink-500 truncate">{p.email}</div>}
                {p.notes && <div className="text-sm text-ink-400 mt-1 whitespace-pre-wrap">{p.notes}</div>}

                {p.recentServices?.length > 0 && (
                  <ul className="mt-2 space-y-0.5">
                    {p.recentServices.map((r) => (
                      <li key={r.id} className="text-sm text-ink-500 truncate">
                        <span className="tabular-nums text-ink-400">{shortDate(r.date)}</span>
                        {' · '}
                        <span className="font-medium text-ink-700">
                          {r.title || RECORD_LABELS[r.recordType] || r.recordType}
                        </span>
                        {' · '}
                        {r.horseName}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {canManage && (
                <div className="flex items-center gap-1 shrink-0">
                  <IconButton icon={PencilIcon} label="Edit" onClick={() => setEditing(p)} />
                  <IconButton
                    icon={TrashIcon}
                    tone="danger"
                    label="Delete"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (confirm(`Delete ${p.name}? This removes them from all horses.`)) {
                        remove.mutate(p.id);
                      }
                    }}
                  />
                </div>
              )}
            </li>
          ),
        )}
      </ul>
    </div>
  );
}

function ProviderForm({ provider, onClose }) {
  const qc = useQueryClient();
  const isEdit = !!provider;
  const [name, setName] = useState(provider?.name ?? '');
  const [businessName, setBusinessName] = useState(provider?.businessName ?? '');
  const [email, setEmail] = useState(provider?.email ?? '');
  const [notes, setNotes] = useState(provider?.notes ?? '');
  const [phones, setPhones] = useState(
    provider?.phones?.length ? provider.phones.map((p) => ({ ...p })) : [{ label: '', number: '' }],
  );
  const [specialties, setSpecialties] = useState(provider?.specialties ?? []);
  const [specInput, setSpecInput] = useState('');

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim(),
        business_name: businessName.trim() || undefined,
        email: email.trim() || undefined,
        notes: notes.trim() || undefined,
        phones: phones
          .filter((p) => p.number.trim())
          .map((p) => ({ label: p.label.trim(), number: p.number.trim() })),
        specialties,
      };
      return isEdit ? api.patch(`/providers/${provider.id}`, payload) : api.post('/providers', payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['providers'] });
      onClose();
    },
  });

  function updatePhone(i, field, value) {
    setPhones((prev) =>
      prev.map((p, idx) =>
        idx === i ? { ...p, [field]: field === 'number' ? formatPhoneInput(value) : value } : p,
      ),
    );
  }
  const addPhone = () => setPhones((prev) => [...prev, { label: '', number: '' }]);
  const removePhone = (i) => setPhones((prev) => prev.filter((_, idx) => idx !== i));

  function addSpecialty() {
    const v = specInput.trim();
    if (v && !specialties.includes(v)) setSpecialties((prev) => [...prev, v]);
    setSpecInput('');
  }
  const removeSpecialty = (s) => setSpecialties((prev) => prev.filter((x) => x !== s));

  const canSave = name.trim() && !save.isPending;

  return (
    <div className="card p-4 mb-4 space-y-3">
      <h2 className="font-medium">{isEdit ? 'Edit provider' : 'New provider'}</h2>

      <input
        className="input"
        placeholder="Name (required)"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <input
        className="input"
        placeholder="Business / clinic (optional)"
        value={businessName}
        onChange={(e) => setBusinessName(e.target.value)}
      />
      <input
        className="input"
        type="email"
        placeholder="Email (optional)"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />

      <div>
        <label className="block text-sm text-ink-600 mb-1">Phone Numbers</label>
        <div className="space-y-2">
          {phones.map((p, i) => (
            <div key={i} className="flex gap-2">
              <input
                className="input w-28"
                placeholder="Label"
                value={p.label}
                onChange={(e) => updatePhone(i, 'label', e.target.value)}
              />
              <input
                className="input flex-1"
                type="tel"
                inputMode="numeric"
                placeholder="(555) 123-4567"
                value={p.number}
                onChange={(e) => updatePhone(i, 'number', e.target.value)}
              />
              <button
                onClick={() => removePhone(i)}
                className="text-ink-300 hover:text-red-600 px-1"
                aria-label="Remove phone"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
        <button onClick={addPhone} className="mt-1 text-sm text-ink-600 hover:text-ink-900">
          + Add Phone
        </button>
      </div>

      <div>
        <label className="block text-sm text-ink-600 mb-1">Specialties</label>
        {specialties.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {specialties.map((s) => (
              <span
                key={s}
                className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 flex items-center gap-1"
              >
                {s}
                <button
                  onClick={() => removeSpecialty(s)}
                  className="text-emerald-500 hover:text-red-600"
                  aria-label={`Remove ${s}`}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input
            className="input flex-1"
            placeholder="Add a specialty, then Enter"
            value={specInput}
            onChange={(e) => setSpecInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addSpecialty();
              }
            }}
          />
          <button onClick={addSpecialty} className="text-sm text-ink-600 hover:text-ink-900 px-2">
            Add
          </button>
        </div>
      </div>

      <textarea
        className="input"
        rows={2}
        placeholder="Notes (optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />

      {save.isError && <p className="text-sm text-red-600">{friendlyError(save.error)}</p>}

      <div className="flex gap-2">
        <button onClick={() => save.mutate()} disabled={!canSave} className="btn-primary">
          {save.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Provider'}
        </button>
        <button onClick={onClose} className="text-sm text-ink-500 hover:text-ink-800 px-2">
          Cancel
        </button>
      </div>
    </div>
  );
}

function friendlyError(err) {
  const code = err?.body?.error || err?.message || '';
  switch (code) {
    case 'missing_name':
      return 'Name is required.';
    case 'invalid_email':
      return 'That email does not look right.';
    default:
      return 'Could not save. Try again.';
  }
}

// Same labels the reminder emails and the photo attachments use, so a record
// reads the same wherever it appears.
const RECORD_LABELS = {
  coggins: 'Coggins',
  vaccination: 'Vaccination',
  farrier: 'Farrier',
  dental: 'Dental',
  vet: 'Vet visit',
  injection: 'Injection',
  injury: 'Injury',
  deworming: 'Deworming',
  other: 'Record',
};

/** "2026-08-12" -> "Aug 12". Was "12 Aug" — day first, which is not the order
 *  the rest of the app reads in. */
const shortDate = (iso) => fmtDate(iso, 'short');
